/**
 * Вложения ЭДО (резюме, скан приказа): имя, вид, размер, дата + «Скачать».
 *
 * Файл отдаётся защищённой ручкой (Bearer + ACL по участникам) — тянем Blob через
 * api.edoAttachment и отдаём его браузеру как objectURL. На нативе скачивания нет (в проекте
 * нет file-system/sharing) — список виден, вместо кнопки подсказка про веб-версию.
 *
 * PDF в вебе можно открыть «Просмотром» (iframe прямо под строкой, PdfViewer.web). Протокол
 * согласования IT_WORKS (kind `protocol`) всегда первым в списке.
 *
 * `canEditOrders` (флаг карточки canEditOrderScans — HR компании заявки / admin после шага
 * приказа, 28.09.2026): «Изменить приказы» — убрать скан, добавить новые (замена = убрать +
 * добавить). Итоговый список уходит одним PUT; пустым он быть не может. Добавлять — только в вебе.
 */
import { useState } from 'react'
import { Platform } from 'react-native'
import { Text, XStack, YStack } from 'tamagui'

import { api } from '../../../api/client'
import { useApp, type Localized } from '../../../i18n'
import { pickFilesWeb } from '../../../lib/files'
import { errorText } from '../errorText'
import { tone } from '../../../theme/tones'
import { Button, Icon, IconButton } from '../../../ui'
import type { EdoAttachment } from '../../../lib/types'
import { useUpdateOrderScans } from '../useEdo'
import { fmtDateTime } from './CardDetails'
import { PdfViewer } from './PdfViewer'

const ATTACH_KIND: Record<string, Localized> = {
  resume: { ru: 'Резюме', en: 'Résumé' },
  order_scan: { ru: 'Скан приказа', en: 'Order scan' },
  signed_scan: { ru: 'Подписанный договор', en: 'Signed contract' },
  revision: { ru: 'Новая редакция', en: 'New revision' },
  certificate: { ru: 'Справка', en: 'Certificate' },
  protocol: { ru: 'Протокол согласования', en: 'Approval protocol', az: 'Razılaşdırma protokolu' },
  // IT-работы: отчёты (и приложения Грузии) и счёт подрядчика.
  report: { ru: 'Отчёт', en: 'Report', az: 'Hesabat' },
  invoice: { ru: 'Счёт подрядчика', en: 'Contractor invoice', az: 'Podratçının hesab-fakturası' },
}

export function AttachmentsBlock({
  attachments,
  requestId,
  canEditOrders,
}: {
  attachments?: EdoAttachment[]
  requestId?: string
  canEditOrders?: boolean
}) {
  const { t } = useApp()
  const [busy, setBusy] = useState<{ id: string; view: boolean } | null>(null)
  // Открытый просмотр PDF: байты уже получены, PdfViewer только показывает их.
  const [view, setView] = useState<{ uploadId: string; blob: Blob; name: string } | null>(null)
  const [err, setErr] = useState('')
  // Правка приказов: uploadId убранных сканов + новые файлы. null — не в режиме правки.
  const [edit, setEdit] = useState<{ removed: string[]; added: File[] } | null>(null)
  const [uploading, setUploading] = useState(false)
  const save = useUpdateOrderScans()
  if (!attachments?.length) return null
  const fmtSize = (b: number) =>
    b >= 1048576
      ? `${(b / 1048576).toFixed(1)} ${t({ ru: 'МБ', en: 'MB' })}`
      : `${Math.max(1, Math.round(b / 1024))} ${t({ ru: 'КБ', en: 'KB' })}`
  const load = async (a: EdoAttachment, isView: boolean, then: (blob: Blob) => void) => {
    if (busy) return
    setErr('')
    setBusy({ id: a.uploadId, view: isView })
    try {
      then(await api.edoAttachment(a.uploadId))
    } catch (e) {
      setErr(t(errorText(e)))
    } finally {
      setBusy(null)
    }
  }
  const download = (a: EdoAttachment) =>
    load(a, false, (blob) => {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = a.upload.name
      link.click()
      // Отложенный revoke: сразу после click браузер мог ещё не начать чтение blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    })
  const preview = (a: EdoAttachment) => load(a, true, (blob) => setView({ uploadId: a.uploadId, blob, name: a.upload.name }))

  const kept = attachments
    .filter((a) => a.kind === 'order_scan' && !edit?.removed.includes(a.uploadId))
    .map((a) => a.uploadId)
  const left = kept.length + (edit?.added.length ?? 0)
  const removeLabel = (name: string) => `${t({ ru: 'Убрать', en: 'Remove', az: 'Sil' })} ${name}`
  const toggle = (uploadId: string) =>
    edit &&
    setEdit({
      ...edit,
      removed: edit.removed.includes(uploadId) ? edit.removed.filter((id) => id !== uploadId) : [...edit.removed, uploadId],
    })
  const submit = async () => {
    if (!edit || !requestId || !left || uploading || save.isPending) return
    setErr('')
    setUploading(true)
    const ids: string[] = []
    try {
      for (const f of edit.added) {
        const form = new FormData()
        form.append('file', f as unknown as Blob)
        ids.push((await api.uploadEdoDoc(form)).id)
      }
    } catch (e) {
      setErr(t(errorText(e)))
      return
    } finally {
      setUploading(false)
    }
    save.mutate({ id: requestId, uploadIds: [...kept, ...ids] }, { onSuccess: () => setEdit(null) })
  }

  // Протокол согласования — первым (sort стабилен: остальные в порядке сервера).
  const ordered = [...attachments].sort((a, b) => Number(b.kind === 'protocol') - Number(a.kind === 'protocol'))
  return (
    <YStack gap={8}>
      {ordered.map((a) => {
        const removed = !!edit?.removed.includes(a.uploadId)
        const mine = busy?.id === a.uploadId
        return (
          <YStack key={a.id} gap={8}>
            <XStack gap={10} alignItems="center" padding={10} borderWidth={1} borderColor="$borderSecondary" borderRadius={7} opacity={removed ? 0.5 : 1}>
              <Icon name="paperclip" size={16} color="$muted" />
              <YStack flex={1} minWidth={0}>
                <Text fontSize={13} fontWeight="600" color="$color" numberOfLines={1} textDecorationLine={removed ? 'line-through' : 'none'}>
                  {a.upload.name}
                </Text>
                <Text fontSize={11} color="$muted" numberOfLines={1}>
                  {t(ATTACH_KIND[a.kind] ?? { ru: 'Файл', en: 'File' })} · {fmtSize(a.upload.size)} · {fmtDateTime(a.createdAt)}
                </Text>
              </YStack>
              {edit && a.kind === 'order_scan' ? (
                <Button size="sm" aria-label={removeLabel(a.upload.name)} onPress={() => toggle(a.uploadId)}>
                  {removed ? t({ ru: 'Вернуть', en: 'Restore', az: 'Qaytar' }) : t({ ru: 'Убрать', en: 'Remove', az: 'Sil' })}
                </Button>
              ) : Platform.OS === 'web' ? (
                <>
                  {a.upload.mime === 'application/pdf' && (
                    <Button
                      size="sm"
                      icon={<Icon name="eye" size={14} color="$muted" />}
                      disabled={mine}
                      opacity={mine ? 0.5 : 1}
                      onPress={() => void preview(a)}
                    >
                      {mine && busy.view ? t({ ru: 'Открываем…', en: 'Opening…', az: 'Açılır…' }) : t({ ru: 'Просмотр', en: 'View', az: 'Bax' })}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    icon={<Icon name="download" size={14} color="$muted" />}
                    disabled={mine}
                    opacity={mine ? 0.5 : 1}
                    onPress={() => void download(a)}
                  >
                    {mine && !busy.view ? t({ ru: 'Скачиваем…', en: 'Downloading…' }) : t({ ru: 'Скачать', en: 'Download' })}
                  </Button>
                </>
              ) : null}
            </XStack>
            {view?.uploadId === a.uploadId && <PdfViewer blob={view.blob} title={view.name} onClose={() => setView(null)} />}
          </YStack>
        )
      })}
      {edit?.added.map((f, i) => (
        <XStack key={`new-${i}-${f.name}`} gap={10} alignItems="center" padding={10} borderWidth={1} borderColor="$primary" borderRadius={7}>
          <Icon name="paperclip" size={16} color="$primary" />
          <Text flex={1} fontSize={13} fontWeight="600" color="$color" numberOfLines={1}>{f.name}</Text>
          <IconButton aria-label={removeLabel(f.name)} onPress={() => setEdit({ ...edit, added: edit.added.filter((_, j) => j !== i) })}>
            <Icon name="x" size={14} color="$muted" />
          </IconButton>
        </XStack>
      ))}
      {Platform.OS !== 'web' && (
        <Text fontSize={11} color="$muted">
          {t({ ru: 'Скачивание вложений доступно в веб-версии портала', en: 'Downloads are available in the web version' })}
        </Text>
      )}

      {canEditOrders && requestId && !edit && (
        <Button size="sm" icon={<Icon name="pencil" size={14} color="$muted" />} onPress={() => setEdit({ removed: [], added: [] })}>
          {t({ ru: 'Изменить приказы', en: 'Edit orders', az: 'Əmrləri dəyiş' })}
        </Button>
      )}
      {edit && (
        <YStack gap={8}>
          <Text fontSize={12} color="$muted">
            {t({
              ru: 'Чтобы заменить приказ, уберите старый файл и добавьте новый. Изменение попадёт в историю заявки.',
              en: 'To replace an order, remove the old file and add a new one. The change is recorded in the request history.',
              az: 'Əmri əvəz etmək üçün köhnə faylı silin və yenisini əlavə edin. Dəyişiklik müraciətin tarixçəsinə düşəcək.',
            })}
          </Text>
          {Platform.OS === 'web' ? (
            <Button
              size="sm"
              icon={<Icon name="paperclip" size={14} color="$muted" />}
              onPress={async () => {
                const picked = await pickFilesWeb('.pdf,.jpg,.jpeg,.png')
                if (picked.length) setEdit((prev) => prev && { ...prev, added: [...prev.added, ...picked] })
              }}
            >
              {t({ ru: 'Добавить файлы', en: 'Add files', az: 'Fayl əlavə et' })}
            </Button>
          ) : (
            <Text fontSize={12} color="$muted">
              {t({ ru: 'Добавить файлы можно в веб-версии портала', en: 'Files can be added in the web version', az: 'Faylları portalın veb versiyasında əlavə etmək olar' })}
            </Text>
          )}
          {!left && (
            <Text fontSize={12} color={tone('red').text}>
              {t({ ru: 'Должен остаться хотя бы один приказ', en: 'At least one order must remain', az: 'Ən azı bir əmr qalmalıdır' })}
            </Text>
          )}
          <XStack gap={10} justifyContent="flex-end">
            <Button size="sm" onPress={() => setEdit(null)}>
              {t({ ru: 'Отмена', en: 'Cancel' })}
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!left || uploading || save.isPending}
              opacity={!left || uploading || save.isPending ? 0.5 : 1}
              onPress={() => void submit()}
            >
              {uploading || save.isPending ? t({ ru: 'Сохраняем…', en: 'Saving…', az: 'Yadda saxlanılır…' }) : t({ ru: 'Сохранить', en: 'Save' })}
            </Button>
          </XStack>
        </YStack>
      )}
      {save.error instanceof Error && <Text fontSize={12} color={tone('red').text}>{t(errorText(save.error))}</Text>}
      {err ? <Text fontSize={12} color={tone('red').text}>{err}</Text> : null}
    </YStack>
  )
}
